import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { LdapManagedUser, LdapGroupInfo, CreateLdapUserResponse } from 'shared';
import { BasicsStep, BasicsData } from './wizard/BasicsStep';
import { GroupsStep } from './wizard/GroupsStep';
import { PasswordStep } from './wizard/PasswordStep';
import { ReviewStep } from './wizard/ReviewStep';
import { CompleteStep } from './wizard/CompleteStep';

type WizardStep = 'basics' | 'groups' | 'password' | 'review' | 'complete';

export interface WizardDraft {
  uid: string;
  givenName: string;
  sn: string;
  mail: string;
  groups: string[];
  gidNumber?: number;
  loginShell?: string;
  passwordMode: 'auto' | 'manual';
  password: string;
  templateUid: string | null;
}

const EMPTY_DRAFT: WizardDraft = {
  uid: '',
  givenName: '',
  sn: '',
  mail: '',
  groups: [],
  passwordMode: 'auto',
  password: '',
  templateUid: null,
};

const STEP_TITLES: Record<WizardStep, string> = {
  basics: 'New LDAP User - Basics',
  groups: 'New LDAP User - Groups',
  password: 'New LDAP User - Password',
  review: 'New LDAP User - Review',
  complete: 'LDAP User Created',
};

const STEP_ORDER: WizardStep[] = ['basics', 'groups', 'password', 'review'];

interface LdapUserWizardProps {
  visible: boolean;
  onHide: () => void;
  onCreated: () => void;
  groups: LdapGroupInfo[];
  users: LdapManagedUser[];
  template?: LdapManagedUser | null;
}

export const LdapUserWizard: React.FC<LdapUserWizardProps> = ({
  visible,
  onHide,
  onCreated,
  groups,
  users,
  template,
}) => {
  const [step, setStep] = useState<WizardStep>('basics');
  const [draft, setDraft] = useState<WizardDraft>(EMPTY_DRAFT);
  const [result, setResult] = useState<CreateLdapUserResponse | null>(null);

  // Template = prefill of groups + posix settings; identity fields stay blank
  const applyTemplate = (source: LdapManagedUser | null) => {
    if (source) {
      setDraft((d) => ({
        ...d,
        groups: [...source.groups],
        gidNumber: source.gidNumber,
        loginShell: source.loginShell,
        templateUid: source.uid,
      }));
    } else {
      setDraft((d) => ({
        ...d,
        groups: [],
        gidNumber: undefined,
        loginShell: undefined,
        templateUid: null,
      }));
    }
  };

  useEffect(() => {
    if (visible && template) {
      applyTemplate(template);
    }
  }, [visible, template]);

  const reset = () => {
    setStep('basics');
    setDraft(EMPTY_DRAFT);
    setResult(null);
  };

  const close = () => {
    reset();
    onHide();
  };

  const handleHide = () => {
    if (step === 'complete') {
      // The generated password is gone after this - completion screen has
      // its own Done button, closing is equivalent
      onCreated();
      close();
      return;
    }
    const dirty = draft.uid || draft.givenName || draft.sn || draft.mail;
    if (dirty) {
      confirmDialog({
        message: 'Discard this new user? All entered data will be lost.',
        header: 'Discard',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        acceptClassName: 'p-button-danger',
        accept: close,
      });
    } else {
      close();
    }
  };

  const stepIndex = STEP_ORDER.indexOf(step);
  const header =
    step === 'complete'
      ? STEP_TITLES.complete
      : `${STEP_TITLES[step]} (${stepIndex + 1}/${STEP_ORDER.length})`;

  return (
    <Dialog
      data-component="ldap-user-wizard"
      header={header}
      visible={visible}
      onHide={handleHide}
      style={{ width: 'min(95vw, 700px)' }}
      modal
      closable
    >
      {step === 'basics' && (
        <BasicsStep
          draft={draft}
          users={users}
          templateLocked={!!template}
          onApplyTemplate={applyTemplate}
          onNext={(basics: BasicsData) => {
            setDraft((d) => ({ ...d, ...basics }));
            setStep('groups');
          }}
          onCancel={handleHide}
        />
      )}
      {step === 'groups' && (
        <GroupsStep
          groups={groups}
          selected={draft.groups}
          onChange={(selected) => setDraft((d) => ({ ...d, groups: selected }))}
          onBack={() => setStep('basics')}
          onNext={() => setStep('password')}
        />
      )}
      {step === 'password' && (
        <PasswordStep
          mode={draft.passwordMode}
          password={draft.password}
          onChange={(mode, password) =>
            setDraft((d) => ({ ...d, passwordMode: mode, password }))
          }
          onBack={() => setStep('groups')}
          onNext={() => setStep('review')}
        />
      )}
      {step === 'review' && (
        <ReviewStep
          draft={draft}
          groups={groups}
          onBack={() => setStep('password')}
          onCreated={(res) => {
            setResult(res);
            setStep('complete');
          }}
        />
      )}
      {step === 'complete' && result && (
        <CompleteStep
          result={result}
          onDone={() => {
            onCreated();
            close();
          }}
        />
      )}
    </Dialog>
  );
};
